import type {
  Balance,
  EmergencyContact,
  EmergencyContactUpdate,
  Leave,
  LeaveStatus,
  Me,
  MyHolidayObservance,
  NotificationPreferences,
  NotificationPreferencesUpdate,
} from '@lms/shared-types'
import { apiRequest } from './client'

export interface ReplacementGrantBreakdown {
  id: string
  remaining: number
  expiresAt: string
}

export interface ReplacementCap {
  remainingDays: number
  nearestExpiry: string | null
  grants: ReplacementGrantBreakdown[]
}

export function getMyReplacementCap(): Promise<ReplacementCap> {
  return apiRequest<ReplacementCap>('/api/v1/me/replacement-cap')
}

export function getMe(): Promise<Me> {
  return apiRequest<Me>('/api/v1/me')
}

export function updatePreferredName(input: { preferredName: string | null }): Promise<Me> {
  return apiRequest<Me>('/api/v1/me/preferred-name', {
    method: 'PATCH',
    body: input,
  })
}

export function listMyBalances(year?: number): Promise<Balance[]> {
  const qs = year != null ? `?year=${year}` : ''
  return apiRequest<Balance[]>(`/api/v1/me/balances${qs}`)
}

export function listMyLeaves(status?: LeaveStatus): Promise<Leave[]> {
  const qs = status ? `?status=${status}` : ''
  return apiRequest<Leave[]>(`/api/v1/me/leaves${qs}`)
}

export interface ApproverRef {
  id: string
  name: string
}

export interface ApproverChain {
  manager: ApproverRef | null
  hod: ApproverRef | null
  director: ApproverRef | null
  execDirector: ApproverRef | null
}

export function getMyApproverChain(leaveType: string): Promise<ApproverChain> {
  // leave_type is required on the server (Phase 5 of the
  // approval-assignments hardening slice). An annual-only EA
  // temporary_cover should only surface when the requester is about to
  // apply for annual — calling without scope used to silently default
  // to annual on the server, so the chip lied for every other type.
  const qs = new URLSearchParams({ leave_type: leaveType }).toString()
  return apiRequest<ApproverChain>(`/api/v1/me/approver-chain?${qs}`)
}

export function getMyHolidayObservance(year?: number): Promise<MyHolidayObservance> {
  const qs = year != null ? `?year=${year}` : ''
  return apiRequest<MyHolidayObservance>(`/api/v1/me/holiday-observance${qs}`)
}

export function getMyEmergencyContact(): Promise<EmergencyContact> {
  return apiRequest<EmergencyContact>('/api/v1/me/emergency-contact')
}

export function putMyEmergencyContact(
  input: EmergencyContactUpdate,
): Promise<EmergencyContact> {
  return apiRequest<EmergencyContact>('/api/v1/me/emergency-contact', {
    method: 'PUT',
    body: input,
  })
}

export interface MeProfilePatch {
  preferredName?: string | null
  hometown?: string | null
  children?: number
}

export function updateMyProfile(patch: MeProfilePatch): Promise<Me> {
  return apiRequest<Me>('/api/v1/me/profile', {
    method: 'PATCH',
    body: patch,
  })
}

export function getMyNotificationPreferences(): Promise<NotificationPreferences> {
  return apiRequest<NotificationPreferences>('/api/v1/me/notification-preferences')
}

export function patchMyNotificationPreferences(
  input: NotificationPreferencesUpdate,
): Promise<NotificationPreferences> {
  return apiRequest<NotificationPreferences>('/api/v1/me/notification-preferences', {
    method: 'PATCH',
    body: input,
  })
}
